import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GfFlyerReviewComment } from "@swagger/typescript-fetch-goflyer";
import { reviewApiService } from "../../../../../utils/api";
import { ReviewChangeRequest, extractList } from "../../types";
import { reviewKeys } from "../../queryKeys";
import {
  buildReviewActivity,
  ItemActivityGroup,
  WorkspaceStats,
} from "./buildReviewActivity";

export type { ItemActivityGroup, WorkspaceStats } from "./buildReviewActivity";

export interface UseReviewActivityResult {
  groups: ItemActivityGroup[];
  flyerComments: GfFlyerReviewComment[];
  workspaceStats: WorkspaceStats;
  initialLoading: boolean;
  refreshing: boolean;
  /** Surfaces a failed change-request or comment fetch (previously swallowed). */
  error: boolean;
  refetch: () => Promise<void>;
}

const EMPTY_CRS: ReviewChangeRequest[] = [];
const EMPTY_COMMENTS: GfFlyerReviewComment[] = [];

export const useReviewActivity = (
  flyerId: string | undefined,
  reqOptions: { headers: { Authorization: string } },
): UseReviewActivityResult => {
  const queryClient = useQueryClient();
  const auth = reqOptions.headers.Authorization;

  const changeRequestsQuery = useQuery({
    queryKey: reviewKeys.changeRequests(flyerId ?? ""),
    queryFn: ({ signal }) =>
      reviewApiService.gfFlyerReviewAdminControllerListChangeRequests(
        flyerId as string,
        undefined,
        undefined,
        undefined,
        { headers: { Authorization: auth }, signal },
      ),
    enabled: !!flyerId,
    staleTime: 0,
    gcTime: 5 * 60_000,
    select: (res) => extractList<ReviewChangeRequest>(res),
  });

  const commentsQuery = useQuery({
    queryKey: reviewKeys.comments(flyerId ?? ""),
    queryFn: ({ signal }) =>
      reviewApiService.gfFlyerReviewAdminControllerListComments(
        flyerId as string,
        undefined,
        undefined,
        undefined,
        { headers: { Authorization: auth }, signal },
      ),
    enabled: !!flyerId,
    staleTime: 0,
    gcTime: 5 * 60_000,
    select: (res) => extractList<GfFlyerReviewComment>(res),
  });

  const changeRequests = changeRequestsQuery.data ?? EMPTY_CRS;
  const comments = commentsQuery.data ?? EMPTY_COMMENTS;

  const { groups, flyerComments, workspaceStats } = useMemo(
    () => buildReviewActivity(changeRequests, comments),
    [changeRequests, comments],
  );

  const refetch = useCallback(async () => {
    if (!flyerId) return;
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: reviewKeys.changeRequests(flyerId),
      }),
      queryClient.invalidateQueries({
        queryKey: reviewKeys.comments(flyerId),
      }),
    ]);
  }, [flyerId, queryClient]);

  const initialLoading =
    changeRequestsQuery.isLoading || commentsQuery.isLoading;
  const refreshing =
    !initialLoading &&
    (changeRequestsQuery.isFetching || commentsQuery.isFetching);

  return {
    groups,
    flyerComments,
    workspaceStats,
    initialLoading,
    refreshing,
    error: changeRequestsQuery.isError || commentsQuery.isError,
    refetch,
  };
};
